<template>
    <Modal
        :model-value="modelValue"
        width="95vw"
        height="95vh"
        class-name="add-templates-modal"
        @onCancel="$emit('update:modelValue', false)"
    >
        <template #header>
            <h3>Master Templates</h3>
        </template>

        <div v-if="masterTemplatesToAdd.length" class="templates-modal__sidebar">
            <MasterTemplateSizeFilter
                class="templates-modal__sidebar-filter"
                :campaign-master-template-ids="masterTemplateIds"
            />

            <MasterTemplateTypeFilter
                class="templates-modal__sidebar-filter"
                :campaign-master-template-ids="masterTemplateIds"
            />

            <MasterTemplateResizedFilter
                class="templates-modal__sidebar-filter"
                :campaign-master-template-ids="masterTemplateIds"
            />

            <MasterTemplateFamilyFilter
                class="templates-modal__sidebar-filter"
                :campaign-master-template-ids="masterTemplateIds"
            />
        </div>

        <div class="templates-modal__body">
            <div class="templates-modal__body-content">
                <MasterTemplateLibrary
                    :campaign-master-template-ids="masterTemplateIds"
                    in-modal
                    class="templates-modal__library"
                    @select="onTemplateSelect"
                    @add="addNewTemplates"
                />
            </div>
        </div>

        <template #footer>
            <Button type="text" size="large" @click="cancelTemplateSelection">Cancel</Button>
            <Button
                type="primary"
                size="large"
                data-testid="master-templates__button-add"
                :disabled="!selectedTemplatesIds.length"
                @click="addNewTemplates"
            >
                Add to campaign
            </Button>
        </template>
    </Modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";

import MasterTemplateSizeFilter from "@/components/Campaign/MasterTemplates/MasterTemplateSizeFilter.vue";
import MasterTemplateTypeFilter from "@/components/Campaign/MasterTemplates/MasterTemplateTypeFilter.vue";
import MasterTemplateResizedFilter from "@/components/Campaign/MasterTemplates/MasterTemplateResizedFilter.vue";
import MasterTemplateFamilyFilter from "@/components/Campaign/MasterTemplates/MasterTemplateFamilyFilter.vue";
import MasterTemplateLibrary from "@/components/Campaign/MasterTemplateLibrary.vue";
import MasterTemplates from "@/services/MasterTemplates";
import bus from "@/bus";
import { EditorEvents } from "@/enums/editor";

export default defineComponent({
    name: "CampaignAddTemplatesModal",
    components: {
        MasterTemplateSizeFilter,
        MasterTemplateTypeFilter,
        MasterTemplateResizedFilter,
        MasterTemplateFamilyFilter,
        MasterTemplateLibrary
    },

    props: {
        modelValue: {
            type: Boolean,
            required: true
        }
    },

    emits: ["update:modelValue"],

    data() {
        return {
            selectedTemplatesIds: [] as string[],
            masterTemplateService: null as null | MasterTemplates
        };
    },

    computed: {
        clientId() {
            return this.$route.params.clientId as string;
        },

        masterTemplateIds() {
            return this.$store.state.campaign.normalized.masterTemplateIds;
        },

        masterTemplatesToAdd() {
            return this.$store.state.masterTemplateLibrary.masterTemplateIds.filter(
                templateId => !this.masterTemplateIds.includes(templateId)
            );
        }
    },

    created() {
        this.masterTemplateService = new MasterTemplates(this.$apollo, this.clientId);
    },

    methods: {
        onTemplateSelect(selectedTemplatesIds: string[]) {
            this.selectedTemplatesIds = selectedTemplatesIds;
        },

        cancelTemplateSelection() {
            this.selectedTemplatesIds = [];
            this.$emit("update:modelValue", false);
        },

        async addNewTemplates() {
            if (!this.selectedTemplatesIds) {
                this.$emit("update:modelValue", false);
                return;
            }

            const templatesIds = this.selectedTemplatesIds.filter(id => !this.masterTemplateIds.includes(id));

            bus.$emit(EditorEvents.AddTemplatesToCampaign, templatesIds);
            this.$emit("update:modelValue", false);
        }
    }
});
</script>
